import { FONTANA_SLIDE_COLORS, PresentationLanguage, PresentationMarket, SIMPLE_FONTS } from './types';
import { LOGICAL_FIELDS, LogicalField } from './fieldCatalog';

export type SettingsColorGroup = 'primary' | 'complement' | 'accent';

export interface SettingsColor {
  id: string;
  hex: string;
  name: string;
  group: SettingsColorGroup;
  hidden?: boolean;
}

export interface SettingsPrompts {
  rewriteSystem: string;
  rewriteUser: string;
  grammarSystem: string;
  translateSystem: string;
  chatSystem: string;
  htmlRule: string;
  refineFit: string;
}

export interface SettingsTextStyle {
  id: 'heading' | 'subheading' | 'body' | 'bullets';
  label: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textTransform: 'none' | 'uppercase';
  lineHeight: number;
  list: 'none' | 'bullet';
}

export interface SettingsImportStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  textTransform: 'none' | 'uppercase';
  lineHeight: number;
}

export interface PresentationSettingsState {
  colors: SettingsColor[];
  allowCustomHex: boolean;
  defaultTextColorId: string;
  defaultShapeColorId: string;
  prompts?: SettingsPrompts;
  textStyles: {
    steps: SettingsTextStyle[];
    newTextStyleId: SettingsTextStyle['id'];
    newTextPlaceholder: string;
    importHeading: SettingsImportStyle;
    importBody: SettingsImportStyle;
  };
  tableDefaults: {
    cols: number;
    rows: number;
    fontSize: number;
    headerBgColorId: string;
    gridColorId: string;
    zebraColorId: string;
  };
  chartDefaults: {
    kind: 'column' | 'bar' | 'line' | 'pie';
    showLegend: boolean;
    showValues: boolean;
    fontSize: number;
    gridColorId: string;
    seriesColorIds: string[];
    background: 'none' | 'white' | 'sand';
  };
  fonts: Array<{ id: string; label: string; hidden: boolean }>;
  health: {
    disabledCodes: string[];
    overflowRatio: number;
    underfillRatio: number;
    rewriteFitMinRatio: number;
  };
  logicalFields: Array<{ id: 'title' | 'articleNumber' | 'measure'; label: string; aliases: string[] }>;
  languages: Array<{ id: string; label: string; visible: boolean }>;
  markets: Array<{ id: string; label: string; visible: boolean }>;
}

function colorIdFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function factoryPresentationSettings(): PresentationSettingsState {
  const colors = FONTANA_SLIDE_COLORS.map((color) => ({
    id: colorIdFromName(color.name),
    hex: color.hex,
    name: color.name,
    group: color.group,
    hidden: false,
  }));
  return {
    colors,
    allowCustomHex: true,
    defaultTextColorId: 'olympia',
    defaultShapeColorId: 'olympia',
    prompts: {
      rewriteSystem: 'You rewrite presentation copy. Keep facts and brand names.',
      rewriteUser: 'Rewrite this presentation HTML so it is clearer and more concise:',
      grammarSystem: 'You correct grammar and spelling in presentation copy. Keep meaning, tone and brand names.',
      translateSystem: 'You translate presentation copy.',
      chatSystem: 'You are a writing assistant for Fontana presentation editors. Answer in Swedish unless asked otherwise. You can answer questions, draft copy, and act as a copywriter. When writing slide text, keep a practical length. No images. No code fences unless the user asks for code.',
      htmlRule: 'The input is slide HTML. Return only HTML using p, br, ul, ol, li, strong, em, u, span. Keep lists, line breaks and reading order. Do not flatten bullets into one paragraph. No markdown, no code fences, no commentary.',
      refineFit: 'The current HTML is {n} visible characters. Rewrite it to {min}-{max} visible characters that fill about {lines} line(s). Keep facts and brand names. Use complete words. For a one-line box write a short headline, not a paragraph and not a single word if a short phrase still fits.',
    },
    textStyles: {
      steps: [
        { id: 'heading', label: 'Rubrik', fontFamily: 'Kapra Neue', fontSize: 96, fontWeight: 600, textTransform: 'uppercase', lineHeight: 1, list: 'none' },
        { id: 'subheading', label: 'Liten rubrik', fontFamily: 'Kapra Neue', fontSize: 48, fontWeight: 600, textTransform: 'uppercase', lineHeight: 1.05, list: 'none' },
        { id: 'body', label: 'Brödtext', fontFamily: 'Akkurat Std', fontSize: 32, fontWeight: 300, textTransform: 'none', lineHeight: 1.1, list: 'none' },
        { id: 'bullets', label: 'Punkter', fontFamily: 'Akkurat Std', fontSize: 31, fontWeight: 300, textTransform: 'none', lineHeight: 1.25, list: 'bullet' },
      ],
      newTextStyleId: 'subheading',
      newTextPlaceholder: 'NY TEXT',
      importHeading: { fontFamily: 'Kapra Neue', fontSize: 72, fontWeight: 600, textTransform: 'uppercase', lineHeight: 1 },
      importBody: { fontFamily: 'Akkurat Std', fontSize: 26, fontWeight: 300, textTransform: 'none', lineHeight: 1.2 },
    },
    tableDefaults: {
      cols: 3,
      rows: 4,
      fontSize: 18,
      headerBgColorId: 'olympia',
      gridColorId: 'sand',
      zebraColorId: 'sand-extra-ljus',
    },
    chartDefaults: {
      kind: 'column',
      showLegend: true,
      showValues: false,
      fontSize: 14,
      gridColorId: 'sand',
      seriesColorIds: ['olympia', 'originalet', 'hetta', 'hav', 'gras', 'bougainvillea'],
      background: 'none',
    },
    fonts: SIMPLE_FONTS.map((font) => ({ id: font.id, label: font.label, hidden: false })),
    health: {
      disabledCodes: [],
      overflowRatio: 1.15,
      underfillRatio: 0.7,
      rewriteFitMinRatio: 0.75,
    },
    logicalFields: LOGICAL_FIELDS.map((field) => ({
      id: field.id,
      label: field.label,
      aliases: [...field.aliases],
    })),
    languages: [
      { id: 'sv', label: 'Svenska', visible: true },
      { id: 'fi', label: 'Finska', visible: true },
      { id: 'en', label: 'Engelska', visible: true },
    ],
    markets: [
      { id: 'SE', label: 'Sverige', visible: true },
      { id: 'FI', label: 'Finland', visible: true },
      { id: 'INT', label: 'Internationellt', visible: true },
    ],
  };
}

export function visibleSettingsColors(settings: PresentationSettingsState): SettingsColor[] {
  return settings.colors.filter((color) => !color.hidden);
}

export function hexForSettingsColor(settings: PresentationSettingsState, id: string, fallback = '#1B3967'): string {
  return settings.colors.find((color) => color.id === id)?.hex || fallback;
}

export function settingsColorIdFromHex(settings: PresentationSettingsState, hex: string, fallback = 'olympia'): string {
  const normalized = String(hex || '').toLowerCase();
  return settings.colors.find((color) => color.hex.toLowerCase() === normalized)?.id || fallback;
}

export function settingsColorOptions(settings: PresentationSettingsState): SettingsColor[] {
  return visibleSettingsColors(settings);
}

export function allowedSettingsHexes(settings: PresentationSettingsState): string[] {
  return visibleSettingsColors(settings).map((color) => color.hex);
}

export function visibleSimpleFonts(settings: PresentationSettingsState, keepId?: string) {
  const hidden = new Set(settings.fonts.filter((font) => font.hidden).map((font) => font.id));
  return SIMPLE_FONTS.filter((font) => !hidden.has(font.id) || font.id === keepId).map((font) => {
    const override = settings.fonts.find((item) => item.id === font.id);
    return { ...font, label: override?.label || font.label };
  });
}

export function visiblePresentationLanguages(settings: PresentationSettingsState): PresentationLanguage[] {
  return settings.languages
    .filter((item) => item.visible && (item.id === 'sv' || item.id === 'fi' || item.id === 'en'))
    .map((item) => item.id as PresentationLanguage);
}

export function visiblePresentationMarkets(settings: PresentationSettingsState): PresentationMarket[] {
  return settings.markets
    .filter((item) => item.visible && (item.id === 'SE' || item.id === 'FI' || item.id === 'INT'))
    .map((item) => item.id as PresentationMarket);
}

export function settingsLanguageLabel(settings: PresentationSettingsState, id: PresentationLanguage): string {
  return settings.languages.find((item) => item.id === id)?.label || '';
}

export function settingsMarketLabel(settings: PresentationSettingsState, id: PresentationMarket): string {
  return settings.markets.find((item) => item.id === id)?.label || '';
}

export function settingsLogicalFields(settings: PresentationSettingsState): LogicalField[] {
  return settings.logicalFields.map((field) => ({
    id: field.id,
    label: field.label,
    aliases: [...field.aliases],
  }));
}
